import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { artProxyUrl } from '../artwork-texture.js';
import { construirePalette, paletteDeRepli } from './cycle.js';

// Shared-rig contract: forbidden. The stone has its own light, one source
// at the top left that the bevels read; a moving rig would flatten them.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'tessera',
  family: 'object',
  danceability: 0.65,
  energyLevel: 0.62,
  bpmMin: 90,
  bpmMax: 150,
  movement: 0.65,
  tonality: 0.80,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: true
};

// Tessera: thousands of small pieces of coloured stone fly in from the void
// and settle in six to eight seconds into the cover as a full-screen
// mosaic, hold, scatter, and start again.
//
// EVERYTHING A PIECE DOES IS A FUNCTION OF ITS OWN ATTRIBUTES AND THE
// PHASE CLOCK, evaluated in the vertex shader: its target, where it flies
// from, its pose time, its flight time, its corner jitter, its final angle,
// its spin, its size, its colour. The CPU writes those once per assembly
// (10 000 x 25 floats, a few milliseconds, deferred to the next frame) and
// then touches nothing but uniforms. That is criterion 7, and it is also
// why ten thousand pieces cost what one quad costs to animate.
//
// THE POSES ARE GROUPED ON THE BEATS. A pattern (radial, sweep, random,
// image, rings) ranks the pieces; the rank spreads them over the assembly's
// sixteen beats; each pose time is then snapped to the nearest beat with
// +-60 ms of scatter. On every beat six or seven hundred pieces clap into
// place, and between beats the next beat's pieces are in the air. The pose
// is a tap: 8% toward the screen and back in 80 ms, with a specular flash
// on the bevel.
//
// THE STONE. Every quad has its four corners displaced (no piece is a
// square), a bevel lit from the top left (the top and left edges brighter,
// the bottom and right darker - the relief), a fine noise at 5% and a hue
// of its own within +-4% (two neighbours of one colour are never the same),
// and 2 px of dark mortar between pieces, which is what makes it read as a
// mosaic. The colours are the cover's, QUANTISED to Cycle's 32-colour
// k-means palette: a real mosaic has a limited stock of stones.

// --- the pieces ----------------------------------------------------------------
const JOINT_PX = 2;
const SIZE_VAR = 0.20;             // +-20% per piece
const JITTER = 0.15;               // corner displacement, of the size
const ROT_DEG = 6;
const FLIGHT_S = [0.8, 1.4];
const POSE_JITTER_S = 0.06;
const OVERSHOOT = 0.08;            // of the size, toward the screen
const OVERSHOOT_S = 0.08;
const FLASH_S = 0.10;
const FLIGHT_BRIGHT = 1.25;
const HOLD_LIT_FRAC = 1 / 200;     // pieces lit per beat in HOLD
const HOLD_LIT_S = 0.35;
const WAVE_WIDTH = 0.18;           // of the frame's diagonal
const LIGHT_DRIFT_S = 90;          // the light source's slow turn
const HUE_VAR = 0.04;
const STONE_NOISE = 0.05;

// --- the cycle ------------------------------------------------------------------
const ASSEMBLE_BEATS = 16;
const ASSEMBLE_S = [6, 8];
const HOLD_PHRASES = 2;
const SCATTER_S = 2.0;
const DROP_SCATTER_S = 0.4;
const DROP_ASSEMBLE_BEATS = 4;
const DROP_LEAD_BEATS = 4;
const BEATS_PER_BAR = 4;
const BEATS_PER_PHRASE = 16;
const SYNTH_BPM = 120;
const PATTERNS = ['radial', 'sweep', 'random', 'image', 'rings'];
const VARIANTS = ['exact', 'zoom', 'negative', 'sepia', 'title'];
const CALM_VARIANTS = ['exact'];
const ZOOM_VARIANT = 1.4;
const CLOUD_Z = [0.8, 3.2];        // the cloud in front of the plane
const SOURCE_PX = 256;             // the cover, resampled for the colours
const PALETTE_PX = 128;            // and for the k-means
const SAFE_FLOOR = 0.55;

const TIER = {
  0: { count: 2500, sizePx: 28, bevel: false, mortar: false, borders: true },
  1: { count: 5000, sizePx: 20, bevel: true, mortar: false, borders: true },
  2: { count: 10000, sizePx: 14, bevel: true, mortar: true, borders: true }
};
const DEFAULT_TIER = 2;
const FALLBACK_VIVID = [0xd94b2b, 0xe8c56a, 0x2f6f8f, 0xf1e6d3];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hexToRgb(hex) { return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255]; }
// a small seeded generator, so an assembly is reproducible for the tests
function lcg(seed) { let s = seed >>> 0 || 1; return function () { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// The Roman borders: three motifs on a cell grid, in the palette's vivid
// colours. Returns a colour index 0..3 (3 = the dark ground).
export function borderMotif(kind, col, row) {
  if (kind === 0) {
    // waves: a sine band two cells thick, on a dark ground
    const y = row - 4 * Math.sin(col * 0.55);
    const band = Math.abs(((y % 7) + 7) % 7 - 3.5);
    return band < 1.2 ? 0 : (band < 2.2 ? 1 : 3);
  }
  if (kind === 1) {
    // greek key (meander): an 8 x 8 tile drawn by rule
    const x = ((col % 8) + 8) % 8, y = ((row % 8) + 8) % 8;
    const key = (y === 0) || (y === 7) || (x === 0 && y <= 5) || (y === 5 && x <= 4) || (x === 4 && y >= 2 && y <= 5) || (y === 2 && x >= 2 && x <= 4) || (x === 6 && y >= 2);
    return key ? 2 : 3;
  }
  // braid: two sine strands crossing every six cells
  const a = Math.abs(row - 2.5 * Math.sin(col * 0.52)) ;
  const b = Math.abs(row + 2.5 * Math.sin(col * 0.52));
  const ma = ((a % 10) + 10) % 10, mb = ((b % 10) + 10) % 10;
  const inA = Math.min(ma, 10 - ma) < 1.3, inB = Math.min(mb, 10 - mb) < 1.3;
  if (inA && inB) return (Math.floor(col / 6) % 2) ? 0 : 1;
  return inA ? 0 : (inB ? 1 : 3);
}

// =============================================================================
// SHADERS
// =============================================================================

const VERTEX = `
  precision mediump float;
  attribute vec2 aTarget;
  attribute vec3 aStart;
  attribute vec3 aCloud;
  attribute float aTime;
  attribute float aFlight;
  attribute vec4 aJitA;
  attribute vec4 aJitB;
  attribute float aRot;
  attribute float aSpin;
  attribute float aSize;
  attribute vec3 aColor;
  attribute float aSeed;
  uniform float uPhase;        // 0 assemble, 1 hold, 2 scatter, 3 cloud
  uniform float uT;            // seconds into the phase
  uniform float uSnapAt;       // pose times past this snap to it (drops)
  uniform float uScatterS;
  uniform float uBeatT;        // seconds since the last beat
  uniform float uBeatIndex;
  uniform float uWave;         // the bar's bevel wave, 0..1, or -1
  uniform vec2 uWaveDir;
  uniform float uWaveSpan;
  uniform float uOvershoot;
  uniform float uLitFrac;
  uniform float uCloudT;
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vLit;
  varying float vFlash;
  varying float vWave;
  varying float vSafe;
  varying float vSeed;
${SAFE_ZONE_VERTEX_CHUNK}
  // Swarm's swirling field: three incommensurate sine products
  vec3 curlish(vec3 p) {
    return vec3(sin(p.y * 1.7) * cos(p.z * 1.3), sin(p.z * 1.1) * cos(p.x * 1.9), sin(p.x * 1.3) * cos(p.y * 1.5));
  }
  vec3 cloudAt(float t) {
    return aCloud + curlish(aCloud * 0.6 + t * 0.15) * 0.7;
  }
  void main() {
    vUv = uv;
    vColor = aColor;
    vSeed = aSeed;
    vec3 pos;
    float ang = aRot;
    float lit = 1.0;
    float flash = 0.0;
    float pop = 1.0;
    if (uPhase < 0.5) {
      // ASSEMBLE: waiting in the cloud, then the flight, then the tap
      float T = min(aTime, uSnapAt);
      float t0 = T - aFlight;
      if (uT < t0) {
        pos = cloudAt(uCloudT + uT);
        ang = aRot + aSpin * (1.0 + uT * 0.3);
        lit = ${FLIGHT_BRIGHT.toFixed(2)};
      } else {
        float s = clamp((uT - t0) / aFlight, 0.0, 1.0);
        // a soft start (smoothstep) and a strong arrival (the squared remainder)
        float e = s * s * (3.0 - 2.0 * s);
        e = 1.0 - (1.0 - e) * (1.0 - e);
        vec3 from = cloudAt(uCloudT + t0);
        vec3 to = vec3(aTarget, 0.0);
        pos = mix(from, to, e);
        // the arc: sideways, the side by the seed, and a rise in front
        vec2 d = to.xy - from.xy;
        float len = max(length(d), 1e-4);
        vec2 perp = vec2(-d.y, d.x) / len;
        float side = aSeed < 0.5 ? -1.0 : 1.0;
        pos.xy += perp * side * 0.18 * len * sin(3.14159 * s);
        pos.z += 0.8 * sin(3.14159 * s) * (1.0 - e);
        ang = aRot + aSpin * (1.0 - e);
        lit = mix(${FLIGHT_BRIGHT.toFixed(2)}, 1.0, step(1.0, s));
        // the tap: 8% toward the screen and back in 80 ms, a flash on the bevel
        if (uT >= T) {
          float u = (uT - T) / ${OVERSHOOT_S.toFixed(3)};
          float k = sin(3.14159 * clamp(u, 0.0, 1.0));
          pos.z += uOvershoot * aSize * k;
          pop = 1.0 + uOvershoot * k;
          flash = 1.0 - clamp((uT - T) / ${FLASH_S.toFixed(3)}, 0.0, 1.0);
        }
      }
    } else if (uPhase < 1.5) {
      // HOLD: in place; a piece in two hundred catches the light on the beat
      pos = vec3(aTarget, 0.0);
      float pick = fract(aSeed * 97.13 + uBeatIndex * 0.6180339);
      float hit = step(pick, uLitFrac);
      flash = hit * (1.0 - clamp(uBeatT / ${HOLD_LIT_S.toFixed(2)}, 0.0, 1.0));
    } else if (uPhase < 2.5) {
      // SCATTER: out to the cloud, radially, turning
      float s = clamp(uT / uScatterS, 0.0, 1.0);
      float e = s * s;
      vec3 to = cloudAt(uCloudT + uT);
      pos = mix(vec3(aTarget, 0.0), to, e);
      ang = aRot + aSpin * 2.0 * e;
      lit = mix(1.0, ${FLIGHT_BRIGHT.toFixed(2)}, s);
    } else {
      // CLOUD: drifting, lit, no image
      pos = cloudAt(uCloudT + uT);
      ang = aRot + aSpin * (2.0 + uT * 0.3);
      lit = ${FLIGHT_BRIGHT.toFixed(2)};
    }
    // the bar's bevel wave across the mosaic (HOLD)
    float proj = dot(aTarget, uWaveDir);
    vWave = uWave < 0.0 ? 0.0 : 1.0 - smoothstep(0.0, ${WAVE_WIDTH.toFixed(2)} * uWaveSpan, abs(proj - (uWave * 2.0 - 1.0) * uWaveSpan * 0.55));
    vLit = lit;
    vFlash = flash;

    // the corner, jittered, scaled, turned
    vec2 c = position.xy;
    vec2 jit = c.x < 0.0 ? (c.y < 0.0 ? aJitA.xy : aJitB.zw) : (c.y < 0.0 ? aJitA.zw : aJitB.xy);
    c += jit;
    float cs = cos(ang), sn = sin(ang);
    vec2 r = vec2(c.x * cs - c.y * sn, c.x * sn + c.y * cs) * aSize * pop;
    vec3 world = pos + vec3(r, 0.0);
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    vec4 centre = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    float att = safeZoneAttenuation(centre);
    vSafe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    gl_Position = clip;
  }
`;

function fragment(bevel) {
  return `
  precision mediump float;
  uniform vec3 uLight;         // toward the source, unit
  uniform float uBevel;
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vLit;
  varying float vFlash;
  varying float vWave;
  varying float vSafe;
  varying float vSeed;
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }
  void main() {
    vec3 col = vColor;
    // the stone's own grain: a fine noise at 5%
    float g = hash21(floor(vUv * 9.0) / 9.0 + fract(vSeed * 3.7));
    col *= 1.0 + ${STONE_NOISE.toFixed(2)} * (g * 2.0 - 1.0);
    ${bevel ? `
    // the bevel: each edge tilts its normal outward; the top and left face
    // the source, the bottom and right face away
    float bw = 0.22;
    float l = 1.0 - smoothstep(0.0, bw, vUv.x);
    float r = 1.0 - smoothstep(0.0, bw, 1.0 - vUv.x);
    float b = 1.0 - smoothstep(0.0, bw, vUv.y);
    float t = 1.0 - smoothstep(0.0, bw, 1.0 - vUv.y);
    float tilt = (l - r) * (-uLight.x) + (t - b) * uLight.y;
    float amp = uBevel * (1.0 + 2.5 * vFlash + 1.6 * vWave);
    col *= 1.0 + amp * tilt;
    // the flat top catches a little of the flash and the wave too
    col *= 1.0 + 0.25 * vFlash + 0.12 * vWave;` : `
    col *= 1.0 + 0.35 * vFlash + 0.15 * vWave;`}
    gl_FragColor = vec4(col * vLit * vSafe, 1.0);
  }
`;
}

const MORTAR_VERTEX = `
  precision mediump float;
  varying vec2 vWorld;
  void main() {
    vWorld = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
function mortarFragment(textured) {
  return `
  precision mediump float;
  varying vec2 vWorld;
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }
  void main() {
    float base = 0.055;
    ${textured ? `float g = hash21(fract(vWorld * 0.37)) * 0.6 + hash21(fract(vWorld * 1.9)) * 0.4;
    base *= 0.75 + 0.5 * g;` : ''}
    gl_FragColor = vec4(vec3(base * 1.02, base, base * 0.95), 1.0);
  }
`;
}

// =============================================================================
// THE VISUALIZER
// =============================================================================

export class TesseraVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._phase = 3;               // starts in the cloud, assembles on the first phrase (or at once)
    this._phaseT = 0;
    this._cloudT = 0;
    this._assembleS = 7;
    this._holdPhrases = 0;
    this._beatClock = 0; this._lastBeat = null; this._lastBarIndex = null; this._lastPhraseIndex = null; this._synthBeat = 0;
    this._beatT = 0; this._beatIndex = 0; this._bpm = SYNTH_BPM;
    this._waveT = 1e9;
    this._lowBand = 0.5; this._highBand = 0.5; this._live = false;
    this._pattern = null; this._variant = null;
    this._paletteRgb = null;       // 32 x 3, 0..1
    this._source = null;           // { data, S } the cover resampled
    this._cover = null;            // the Image
    this._lastArtworkUrl = null; this._lastPalette = null;
    this._vivid = FALLBACK_VIVID.slice();
    this._title = ''; this._artist = '';
    this._progress = 0;
    this._pendingBuild = false;
    this._built = false;
    this._dropLead = 0; this._lastDropTime = -1e9; this._snapAt = 1e9;
    this._seed = 1;
    this._rng = lcg(12345);
    this._assemblies = 0;

    this._forcedPattern = PATTERNS.indexOf(urlParam('tesspattern')) >= 0 ? urlParam('tesspattern') : null;
    this._forcedVariant = VARIANTS.indexOf(urlParam('tessvariant')) >= 0 ? urlParam('tessvariant') : null;
    this._debugAssemble = urlParam('tessassemble') === '1';
    this._debugScatter = urlParam('tessscatter') === '1';
    const fz = urlParam('tessfreeze');
    this._freeze = fz != null && fz !== '' ? clamp01(parseFloat(fz)) : null;
    this._postProcessing = { bloomStrength: 0.3, vignette: 0.3, aberration: 0.0008, grain: 0.02 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, 10);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    const HH = 10 * Math.tan(camera.fov * Math.PI / 360);
    this._HH = HH;
    this._aspect = safeZoneUniforms.uAspect.value || 1.78;
    this._HW = HH * this._aspect;
    this._px = 2 * HH / 720;
    this._bufH = 720;

    const N = this._layout.count;
    this._N = N;
    const geo = new THREE.InstancedBufferGeometry();
    const quad = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
    const uv = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
    geo.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const mk = (n, size) => { const a = new THREE.InstancedBufferAttribute(new Float32Array(N * size), size); a.setUsage(THREE.StaticDrawUsage); geo.setAttribute(n, a); return a; };
    this._attr = {
      aTarget: mk('aTarget', 2), aStart: mk('aStart', 3), aCloud: mk('aCloud', 3), aTime: mk('aTime', 1), aFlight: mk('aFlight', 1),
      aJitA: mk('aJitA', 4), aJitB: mk('aJitB', 4), aRot: mk('aRot', 1), aSpin: mk('aSpin', 1), aSize: mk('aSize', 1), aColor: mk('aColor', 3), aSeed: mk('aSeed', 1)
    };
    geo.instanceCount = 0;
    this._geo = geo;
    this._uniforms = Object.assign({
      uPhase: { value: 3 }, uT: { value: 0 }, uSnapAt: { value: 1e9 }, uScatterS: { value: SCATTER_S },
      uBeatT: { value: 0 }, uBeatIndex: { value: 0 }, uWave: { value: -1 }, uWaveDir: { value: new THREE.Vector2(1, 0) }, uWaveSpan: { value: HH * 2 },
      uOvershoot: { value: OVERSHOOT }, uLitFrac: { value: HOLD_LIT_FRAC }, uCloudT: { value: 0 },
      uLight: { value: new THREE.Vector3(-0.55, 0.6, 0.58).normalize() }, uBevel: { value: this._layout.bevel ? 0.55 : 0 }
    }, safeZoneUniforms);
    this._material = new THREE.ShaderMaterial({ uniforms: this._uniforms, vertexShader: VERTEX, fragmentShader: fragment(this._layout.bevel), depthTest: true, depthWrite: true });
    this._mesh = new THREE.Mesh(geo, this._material);
    this._mesh.frustumCulled = false;
    const self = this;
    this._mesh.onBeforeRender = function (renderer) { renderer.getDrawingBufferSize(_size); self._bufH = Math.max(1, _size.y); };
    scene.add(this._mesh);

    this._mortar = new THREE.Mesh(new THREE.PlaneGeometry(this._HW * 2.4, HH * 2.4), new THREE.ShaderMaterial({ vertexShader: MORTAR_VERTEX, fragmentShader: mortarFragment(this._layout.mortar) }));
    this._mortar.position.z = -0.05;
    this._mortar.renderOrder = -1;
    scene.add(this._mortar);

    // the cloud the first assembly flies from: off-frame and in front
    this._cloud = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const r = this._rng();
      if (r < 0.5) { this._cloud[i * 3] = (this._rng() * 2 - 1) * this._HW * 1.3; this._cloud[i * 3 + 1] = (this._rng() < 0.5 ? -1 : 1) * HH * (1.2 + this._rng() * 0.6); }
      else { this._cloud[i * 3] = (this._rng() * 2 - 1) * this._HW * 1.6; this._cloud[i * 3 + 1] = (this._rng() * 2 - 1) * HH * 1.1; }
      this._cloud[i * 3 + 2] = lerp(CLOUD_Z[0], CLOUD_Z[1], this._rng());
    }
    this._paletteRgb = this._paletteFrom(paletteDeRepli(this._vivid));
    // the first assembly goes through _startAssemble (its duration follows
    // the bpm), on the first frames rather than on a phrase
  }

  _paletteFrom(pal) {
    const out = new Float32Array(32 * 3);
    for (let i = 0; i < 32; i++) { out[i * 3] = pal[i * 4] / 255; out[i * 3 + 1] = pal[i * 4 + 1] / 255; out[i * 3 + 2] = pal[i * 4 + 2] / 255; }
    return out;
  }

  // --- the cover ---------------------------------------------------------------
  _loadCover(url) {
    if (typeof Image === 'undefined' || typeof document === 'undefined') return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const self = this;
    img.onload = function () {
      if (self._lastArtworkUrl !== url || !self._scene) return;
      try {
        const c = document.createElement('canvas');
        c.width = PALETTE_PX; c.height = PALETTE_PX;
        const g = c.getContext('2d', { willReadFrequently: true });
        g.drawImage(img, 0, 0, PALETTE_PX, PALETTE_PX);
        self._paletteRgb = self._paletteFrom(construirePalette(g.getImageData(0, 0, PALETTE_PX, PALETTE_PX).data));
        self._cover = img;
      } catch (err) {
        self._cover = null;      // a tainted canvas: the title or the pattern instead
      }
      // a fresh cover mid-cycle: the next assembly takes it
    };
    img.onerror = function () { self._cover = null; };
    img.src = artProxyUrl(url);
  }

  // The source picture for one assembly: the cover through the variant, or
  // the title, or the Roman pattern - SOURCE_PX square, RGBA.
  _makeSource(variant) {
    if (typeof document === 'undefined') return null;
    const S = SOURCE_PX;
    const c = document.createElement('canvas');
    c.width = S; c.height = S;
    const g = c.getContext('2d', { willReadFrequently: true });
    g.fillStyle = '#101010';
    g.fillRect(0, 0, S, S);
    const cover = this._cover;
    const drawCover = (x, y, w, h, zoom) => {
      if (!cover) return;
      const iw = cover.naturalWidth || cover.width, ih = cover.naturalHeight || cover.height;
      const side = Math.min(iw, ih) / (zoom || 1);
      g.drawImage(cover, (iw - side) / 2, (ih - side) / 2, side, side, x, y, w, h);
    };
    const text = (this._title || '').trim() || 'PIRADIO';
    if (!cover || variant === 'title') {
      if (cover) { drawCover(0, 0, S, S * 0.34, 1); drawCover(0, S * 0.66, S, S * 0.34, 1); }
      else {
        // no cover: the Roman pattern, then the title over it
        const cols = [...this._vivid.map(hexToRgb), [0.08, 0.08, 0.09]];
        for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
          const k = borderMotif(1, x, y); const cc = cols[k];
          g.fillStyle = 'rgb(' + Math.round(cc[0] * 255) + ',' + Math.round(cc[1] * 255) + ',' + Math.round(cc[2] * 255) + ')';
          g.fillRect(x * S / 16, y * S / 16, S / 16 + 1, S / 16 + 1);
        }
      }
      g.fillStyle = '#141210';
      g.fillRect(0, S * 0.34, S, S * 0.32);
      g.fillStyle = '#f2e9d6';
      g.textAlign = 'center'; g.textBaseline = 'middle';
      let size = 64;
      do { g.font = 'bold ' + size + 'px sans-serif'; size -= 4; } while (g.measureText(text).width > S * 0.92 && size > 18);
      g.fillText(text.toUpperCase(), S / 2, S * 0.5);
    } else if (variant === 'zoom') {
      drawCover(0, 0, S, S, ZOOM_VARIANT);
    } else {
      drawCover(0, 0, S, S, 1);
    }
    let data = g.getImageData(0, 0, S, S).data;
    // the negative of a dark cover is mostly white, and white stones under
    // the bloom wash the frame: held at 80%
    if (cover && variant === 'negative') { for (let i = 0; i < data.length; i += 4) { data[i] = (255 - data[i]) * 0.8; data[i + 1] = (255 - data[i + 1]) * 0.8; data[i + 2] = (255 - data[i + 2]) * 0.8; } }
    if (cover && variant === 'sepia') {
      for (let i = 0; i < data.length; i += 4) {
        const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        data[i] = Math.min(255, l * 1.12 + 22); data[i + 1] = Math.min(255, l * 0.94 + 8); data[i + 2] = Math.min(255, l * 0.72);
      }
    }
    return { data, S };
  }

  // --- the assembly: the attributes, once ----------------------------------------
  _pickVariant() {
    if (this._forcedVariant) return this._forcedVariant;
    const calm = this._progress < 0.2 || this._progress > 0.8;
    let pool = calm ? CALM_VARIANTS : VARIANTS;
    pool = pool.filter((v) => v !== this._variant);
    if (!pool.length) pool = VARIANTS.filter((v) => v !== this._variant);
    return pool[Math.floor(this._rng() * pool.length)];
  }
  _pickPattern() {
    if (this._forcedPattern) return this._forcedPattern;
    const pool = PATTERNS.filter((p) => p !== this._pattern);
    return pool[Math.floor(this._rng() * pool.length)];
  }

  _build() {
    const L = this._layout;
    const HH = this._HH, HW = this._HW;
    const px = 2 * HH / this._bufH;
    this._px = px;
    // the size in pixels follows the screen's height (14 px at 1080p), never
    // under 8 px; the count then follows, capped by the tier - or the size
    // grows until the count fits
    let sizePx = Math.max(8, L.sizePx * this._bufH / 1080);
    let pitch = (sizePx + JOINT_PX) * px;
    let cols = Math.ceil(2 * HW / pitch), rows = Math.ceil(2 * HH / pitch);
    while (cols * rows > L.count) { sizePx *= 1.04; pitch = (sizePx + JOINT_PX) * px; cols = Math.ceil(2 * HW / pitch); rows = Math.ceil(2 * HH / pitch); }
    const N = cols * rows;
    this._cols = cols; this._rows = rows; this._sizePx = sizePx; this._pitch = pitch;
    const size = sizePx * px;

    const variant = this._pickVariant();
    const pattern = this._pickPattern();
    this._variant = variant; this._pattern = pattern;
    const src = this._makeSource(variant);
    const pal = this._paletteRgb;
    const vivid = this._vivid.map(hexToRgb);
    const borderKind = Math.floor(this._rng() * 3);
    const rng = lcg(Math.floor(this._rng() * 1e9) + 1);

    const A = this._attr;
    const target = A.aTarget.array, start = A.aStart.array, cloud = A.aCloud.array, time = A.aTime.array, flight = A.aFlight.array;
    const jitA = A.aJitA.array, jitB = A.aJitB.array, rot = A.aRot.array, spin = A.aSpin.array, sz = A.aSize.array, col = A.aColor.array, seed = A.aSeed.array;
    const rank = new Float32Array(N);
    const x0 = -(cols - 1) * pitch / 2, y0 = -(rows - 1) * pitch / 2;
    const coverHalf = HH;                       // the cover fills the height, centred
    const S = src ? src.S : 0;
    const cellS = src ? Math.max(1, Math.round(pitch / (2 * HH) * S)) : 1;
    for (let i = 0; i < N; i++) {
      const cx = i % cols, cy = Math.floor(i / cols);
      const x = x0 + cx * pitch, y = y0 + cy * pitch;
      target[i * 2] = x; target[i * 2 + 1] = y;
      // colour: the cover under the piece, or the border motif
      let r, g, b;
      const inCover = Math.abs(x) <= coverHalf + pitch * 0.5;
      if (inCover && src) {
        const u = (x + coverHalf) / (2 * coverHalf), v = 1 - (y + coverHalf) / (2 * coverHalf);
        const sx = Math.round(u * (S - 1)), sy = Math.round(v * (S - 1));
        let rr = 0, gg = 0, bb = 0, n = 0;
        const half = cellS >> 1;
        for (let dy = -half; dy <= half; dy++) { const yy = sy + dy; if (yy < 0 || yy >= S) continue; for (let dx = -half; dx <= half; dx++) { const xx = sx + dx; if (xx < 0 || xx >= S) continue; const o = (yy * S + xx) * 4; rr += src.data[o]; gg += src.data[o + 1]; bb += src.data[o + 2]; n++; } }
        rr /= 255 * n; gg /= 255 * n; bb /= 255 * n;
        // quantised to the 32 stones
        let best = 0, bd = 1e9;
        for (let k = 0; k < 32; k++) { const dr = pal[k * 3] - rr, dg = pal[k * 3 + 1] - gg, db = pal[k * 3 + 2] - bb; const d = dr * dr * 0.3 + dg * dg * 0.59 + db * db * 0.11; if (d < bd) { bd = d; best = k; } }
        r = pal[best * 3]; g = pal[best * 3 + 1]; b = pal[best * 3 + 2];
      } else {
        const bc = x < 0 ? cx : cols - 1 - cx;
        const k = L.borders ? borderMotif(borderKind, bc, cy) : 3;
        const c = k === 3 ? [0.10, 0.09, 0.085] : vivid[k % vivid.length];
        r = c[0]; g = c[1]; b = c[2];
      }
      // the hue of its own: +-4% on each channel, a different way per piece
      const hv = HUE_VAR;
      col[i * 3] = clamp01(r * (1 + hv * (rng() * 2 - 1)));
      col[i * 3 + 1] = clamp01(g * (1 + hv * (rng() * 2 - 1)));
      col[i * 3 + 2] = clamp01(b * (1 + hv * (rng() * 2 - 1)));
      // the shape
      for (let k = 0; k < 4; k++) { const arr = k < 2 ? jitA : jitB; const o = i * 4 + (k % 2) * 2; arr[o] = (rng() * 2 - 1) * JITTER; arr[o + 1] = (rng() * 2 - 1) * JITTER; }
      rot[i] = (rng() * 2 - 1) * ROT_DEG * Math.PI / 180;
      spin[i] = (rng() * 2 - 1) * Math.PI * 1.5;
      sz[i] = size * (1 + SIZE_VAR * (rng() * 2 - 1));
      seed[i] = rng();
      flight[i] = lerp(FLIGHT_S[0], FLIGHT_S[1], rng());
      // where it flies from: the cloud it sits in
      const ci = i % this._N;
      cloud[i * 3] = this._cloud[ci * 3]; cloud[i * 3 + 1] = this._cloud[ci * 3 + 1]; cloud[i * 3 + 2] = this._cloud[ci * 3 + 2];
      start[i * 3] = cloud[i * 3]; start[i * 3 + 1] = cloud[i * 3 + 1]; start[i * 3 + 2] = cloud[i * 3 + 2];
      // the rank, by the pattern
      const lum = 0.3 * r + 0.59 * g + 0.11 * b;
      const dist = Math.hypot(x / HW, y / HH);
      switch (pattern) {
        case 'radial': rank[i] = dist; break;
        case 'sweep': rank[i] = this._sweepAxis ? (y + HH) / (2 * HH) : (x + HW) / (2 * HW); break;
        case 'random': rank[i] = rng(); break;
        case 'image': rank[i] = lum + 0.05 * rng(); break;
        default: { const ring = Math.floor(dist * 8); rank[i] = (ring % 2 === 0 ? 0 : 0.5) + (ring / 16) + 0.02 * rng(); }
      }
    }
    // pose times: the rank spread over the assembly, snapped to beats
    const order = new Uint32Array(N); for (let i = 0; i < N; i++) order[i] = i;
    const rk = rank;
    order.sort((a, b2) => rk[a] - rk[b2]);
    const spb = 60 / this._bpm;
    const dur = this._assembleS;
    // the rank runs from half a beat to half a beat past the last, so that
    // rounding fills every beat's bin equally: sixteen waves of N/16
    const beats = Math.max(1, Math.round(dur / spb));
    for (let j = 0; j < N; j++) {
      const i = order[j];
      const t = spb * (0.5 + (j + 0.5) / N * beats);
      const snapped = Math.max(spb, Math.round(t / spb) * spb) + (rng() * 2 - 1) * POSE_JITTER_S;
      time[i] = Math.min(dur + POSE_JITTER_S, snapped);
    }
    for (const k in A) A[k].needsUpdate = true;
    this._geo.instanceCount = N;
    this._count = N;
    this._built = true;
    this._assemblies++;
    this._sweepAxis = !this._sweepAxis;
  }

  // --- the clock ----------------------------------------------------------------
  _advanceClock(dt, profile) {
    const hasGrid = profile.beatIndex != null && profile.beatPhase != null;
    let beatClock;
    if (hasGrid) {
      beatClock = profile.beatIndex + profile.beatPhase;
      if (this._lastBeat != null && Math.abs(beatClock - this._beatClock) > 4) { this._lastBeat = null; this._lastBarIndex = null; this._lastPhraseIndex = null; }
    } else {
      this._synthBeat += dt * this._bpm / 60;
      beatClock = this._synthBeat;
    }
    this._beatClock = beatClock;
    const beat = Math.floor(beatClock);
    this._beatT = (beatClock - beat) * 60 / this._bpm;
    if (this._lastBeat == null) { this._lastBeat = beat; return; }
    while (this._lastBeat < beat) {
      this._lastBeat++;
      this._beatIndex++;
      this._onBeat();
      if (hasGrid) {
        const bar = profile.barIndex;
        if (bar != null && bar !== this._lastBarIndex) { if (this._lastBarIndex != null) this._onBar(); this._lastBarIndex = bar; }
        const phrase = profile.phraseIndex;
        if (phrase != null && phrase !== this._lastPhraseIndex) { if (this._lastPhraseIndex != null) this._onPhrase(); this._lastPhraseIndex = phrase; }
      } else {
        if (this._lastBeat % BEATS_PER_BAR === 0) this._onBar();
        if (this._lastBeat % BEATS_PER_PHRASE === 0) this._onPhrase();
      }
    }
  }
  _onBeat() {}
  _onBar() { if (this._phase === 1) this._waveT = 0; }
  _onPhrase() {
    if (this._freeze != null) return;
    if (this._phase === 3) this._startAssemble(null);
    else if (this._phase === 1) { this._holdPhrases++; if (this._holdPhrases >= HOLD_PHRASES) this._startScatter(SCATTER_S); }
  }

  _startAssemble(seconds) {
    const spb = 60 / this._bpm;
    this._assembleS = seconds != null ? seconds : Math.max(ASSEMBLE_S[0], Math.min(ASSEMBLE_S[1], ASSEMBLE_BEATS * spb));
    this._phase = 0; this._phaseT = 0;
    this._snapAt = 1e9;
    this._pendingBuild = true;          // deferred: the attributes are written on the next frame
  }
  _startScatter(seconds) {
    this._phase = 2; this._phaseT = 0;
    this._uniforms.uScatterS.value = seconds;
    this._scatterS = seconds;
    // a fresh cloud to go to
    const N = this._N, HH = this._HH;
    for (let i = 0; i < N; i++) {
      this._cloud[i * 3] = (this._rng() * 2 - 1) * this._HW * 1.5;
      this._cloud[i * 3 + 1] = (this._rng() * 2 - 1) * HH * 1.3;
      this._cloud[i * 3 + 2] = lerp(CLOUD_Z[0], CLOUD_Z[1], this._rng());
    }
    const cloud = this._attr.aCloud.array;
    for (let i = 0; i < this._count; i++) { const ci = i % N; cloud[i * 3] = this._cloud[ci * 3]; cloud[i * 3 + 1] = this._cloud[ci * 3 + 1]; cloud[i * 3 + 2] = this._cloud[ci * 3 + 2]; }
    this._attr.aCloud.needsUpdate = true;   // once per scatter, not per frame
    this._holdPhrases = 0;
  }

  // --- the frame ---------------------------------------------------------------
  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    this._time += dt;

    if (profile.artworkPalette !== this._lastPalette) {
      this._lastPalette = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors;
      this._vivid = v && v.length ? v.slice(0, 4) : FALLBACK_VIVID.slice();
      while (this._vivid.length < 3) this._vivid.push(FALLBACK_VIVID[this._vivid.length]);
      if (!this._cover) this._paletteRgb = this._paletteFrom(paletteDeRepli(this._vivid));
    }
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadCover(profile.artworkUrl); else this._cover = null;
    }
    this._title = profile.title || ''; this._artist = profile.artist || '';
    this._progress = clamp01(profile.progress || 0);
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    const spb = 60 / this._bpm;
    const director = profile.director || {};
    this._live = !!(director.hasReliableGrid && director.bands);
    if (this._live) { this._lowBand = clamp01(director.bands.low); this._highBand = clamp01(director.bands.high); }
    this._advanceClock(dt, profile);

    // the first assembly waits for nothing
    if (this._assemblies === 0 && this._phase === 3 && this._time > 0.3 && !this._pendingBuild) this._startAssemble(null);
    if (this._debugAssemble && this._time > 1.0 && this._phase !== 0 && !this._debugDone) { this._debugDone = true; this._startAssemble(null); }
    if (this._debugScatter && this._time > 1.0 && this._phase !== 2 && !this._debugDone) { this._debugDone = true; if (this._phase === 3) this._startAssemble(null); else this._startScatter(SCATTER_S); }

    // the deferred build: one frame after the phase change
    if (this._pendingBuild) { this._pendingBuild = false; this._build(); if (this._phase !== 0) this._phase = 0; this._phaseT = 0; }

    // the drop
    const ev = director.nextEvent;
    this._dropLead = 0;
    if (ev && ev.type === 'drop' && ev.msUntil != null) {
      const lead = DROP_LEAD_BEATS * spb * 1000;
      if (ev.msUntil > 0 && ev.msUntil <= lead) this._dropLead = 1;
      if (ev.msUntil <= 0 && this._time - this._lastDropTime > 2) {
        this._lastDropTime = this._time;
        if (this._phase === 1) { this._startScatter(DROP_SCATTER_S); this._dropReassemble = true; }
        else if (this._phase === 0) this._snapAt = this._phaseT;      // every piece still out snaps in now
      }
    }

    // the phase clock
    const scale = this._phase === 0 && this._dropLead ? 2 : 1;
    this._phaseT += dt * scale;
    this._cloudT += dt;
    if (this._freeze != null && this._phase === 0) this._phaseT = this._freeze * this._assembleS;
    if (this._phase === 0 && this._phaseT >= this._assembleS + FLIGHT_S[1] && this._freeze == null) { this._phase = 1; this._phaseT = 0; this._holdPhrases = 0; }
    if (this._phase === 2 && this._phaseT >= this._scatterS) {
      this._phase = 3; this._phaseT = 0;
      if (this._dropReassemble) { this._dropReassemble = false; this._startAssemble(DROP_ASSEMBLE_BEATS * spb); }
    }

    // the uniforms - the only per-frame writes
    const U = this._uniforms;
    U.uPhase.value = this._phase;
    U.uT.value = this._phaseT;
    U.uSnapAt.value = this._snapAt;
    U.uCloudT.value = this._cloudT;
    U.uBeatT.value = this._beatT;
    U.uBeatIndex.value = this._beatIndex;
    U.uOvershoot.value = OVERSHOOT * (this._live ? 0.6 + 0.8 * this._lowBand : 1);
    U.uLitFrac.value = HOLD_LIT_FRAC * (this._live ? 0.5 + 1.5 * this._highBand : 1);
    this._waveT += dt;
    const waveDur = BEATS_PER_BAR * spb * 0.8;
    U.uWave.value = this._phase === 1 && this._waveT < waveDur ? this._waveT / waveDur : -1;
    U.uWaveDir.value.set(Math.cos(this._beatIndex * 0.7), Math.sin(this._beatIndex * 0.7)).normalize();
    U.uWaveSpan.value = Math.hypot(this._HW, this._HH);
    const la = this._time / LIGHT_DRIFT_S * Math.PI * 2;
    U.uLight.value.set(-0.55 + 0.18 * Math.sin(la), 0.6 + 0.12 * Math.cos(la * 0.7), 0.58).normalize();
  }

  // --- for the tests ------------------------------------------------------------
  get state() { return { phase: this._phase, phaseT: this._phaseT, assembleS: this._assembleS, pattern: this._pattern, variant: this._variant, count: this._count, cols: this._cols, rows: this._rows, sizePx: this._sizePx, snapAt: this._snapAt, holdPhrases: this._holdPhrases, assemblies: this._assemblies, pending: this._pendingBuild }; }
  get attributes() { return this._attr; }
  get uniforms() { return this._uniforms; }
  get frame() { return { HH: this._HH, HW: this._HW, px: this._px, pitch: this._pitch }; }

  dispose() {
    if (!this._scene) return;
    this._scene.remove(this._mesh); this._scene.remove(this._mortar);
    this._geo.dispose(); this._material.dispose();
    this._mortar.geometry.dispose(); this._mortar.material.dispose();
    this._mesh = null; this._scene = null;
  }
}

const _size = new THREE.Vector2();
